import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ROUTES } from '../../../routes/urls';
import { logout } from '../../../services/authServices';
import { Buttons, MainDiv, Profile, UserName, WorkspaceIcon } from './styles';
import { getIsDarkMode, isElectronApp, isEmpty } from '../../../helpers/common';
import { COLOR_THEME, COLOR_THEME_TYPE, ICON_SIZE, TAB_BUTTON_TYPE, THEME_STATES } from '../../../global/constants';
import UserPreferenceSingleton from '../../../helpers/userPreferenceSingleton';
import Dropdown from '../../dropdown/mainDropdown';
import DropdownDivider from '../../dropdown/dropdownDivider';
import DropdownItem from '../../dropdown/dropdownItem';
import { useMobileDevice } from '../../../global/useMobile';
import HelpSupportDropdown from '../../navbar/helpSupportDropdown';
import Modal from '../../modals/modal';
import { ContactUsModal } from '../../modals/contactUsmodal';
import { SuccessFullModal } from '../../modals/contactUsmodal/successfullModal';
import { rootStore } from '../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import Tabs from '../../tabs';
import { themeOptions } from '../../../global/row';
import Image from '../../image';

type ThemeStatesType = {
  light: boolean;
  dark: boolean;
  system: boolean;
};
const ProfileDropdown: React.FC = () => {
  // type
  type ThemeKey = 'light' | 'dark' | 'system';
  //State
  const [isActive, setIsActive] = useState<ThemeStatesType>({ light: false, dark: false, system: false });
  const [openContactUsModal, setOpenContactUsModal] = useState(false);
  const [isSuccessfulModalOpen, setIsSuccessfulModalOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  //Mobx store variables
  const { workspaceStore, appStore } = rootStore;
  const { themeType } = appStore;
  const { currentWorkspace: workspace } = workspaceStore;

  //Other variable
  const workspaceNavDropdownRef = useRef<{ closeDropdown: () => void }>(null);
  const history = useHistory();
  const mobile = useMobileDevice();

  //load theme data
  const loadThemeData = useCallback(() => {
    // Get theme mode from local storage
    const themeMode = UserPreferenceSingleton.getInstance().getThemeMode();
    if (themeMode) {
      appStore.setThemeType(themeMode as number);
    }
  }, [appStore]);

  useEffect(() => {
    loadThemeData();
    if (themeType === COLOR_THEME.LIGHT) {
      setIsActive(THEME_STATES?.light);
    } else if (themeType === COLOR_THEME.DARK) {
      setIsActive(THEME_STATES?.dark);
    } else if (themeType === COLOR_THEME.SYSTEM_DEFAULT) {
      setIsActive(THEME_STATES?.system);
    }
  }, [loadThemeData, themeType]);

  //logout the user
  const onClickLogout = useCallback(async () => {
    await logout();
    history.push(ROUTES.LOGIN);
    window.location.reload();
  }, [history]);

  //handle theme change
  const handleThemeChange = useCallback(
    (theme: ThemeKey, themeMode: number, themeTypeValue?: string) => {
      let resolvedThemeType;
      if (themeTypeValue) {
        resolvedThemeType = themeTypeValue;
      } else if (getIsDarkMode()) {
        resolvedThemeType = COLOR_THEME_TYPE.DARK;
      } else {
        resolvedThemeType = COLOR_THEME_TYPE.LIGHT;
      }
      setIsActive(THEME_STATES[theme]);
      UserPreferenceSingleton.getInstance().setThemeMode(themeMode);
      appStore.setThemeMode(resolvedThemeType);
      appStore.setThemeType(themeMode);
      if (window.electronApi?.changeTheme) {
        window.electronApi.changeTheme(theme);
      }
    },
    [appStore]
  );

  //organization name click
  const onclick = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const onClickSetting = useCallback(() => {
    if (mobile) history.push(ROUTES.SETTING);
    else history.push(ROUTES.PERSONAL_SETTING);
  }, [history, mobile]);

  const onCloseContactUsModal = useCallback(() => {
    setOpenContactUsModal(false);
  }, []);

  const onCloseSuccessfulModal = useCallback(() => {
    setIsSuccessfulModalOpen(false);
  }, []);

  return (
    <>
      <MainDiv className='profile-dropdown'>
        <Dropdown
          activeClassName={isElectronApp() ? 'desktop-active' : 'active'}
          ref={workspaceNavDropdownRef}
          modalTitle='Manage Organisation'
          isMinWidth={198}
          content={
            <Profile role='button' onClick={onclick} className='desktop-profile'>
              {!isEmpty(workspace?.logoUrl) ? (
                <Image
                  imageUrl={workspace?.logoUrl || ''}
                  alt='profilePhoto'
                  className='profile-image'
                  width={20}
                  height={20}
                />
              ) : (
                <WorkspaceIcon>
                  <SVGIcon
                    name='workspace-icon'
                    width={ICON_SIZE.XS}
                    height={ICON_SIZE.XS}
                    viewBox='0 0 36 36'
                    className='workspace-icon'
                    fill='var(--text-primary)'
                  />
                </WorkspaceIcon>
              )}
              <UserName>{workspace?.name}</UserName>
            </Profile>
          }>
          <Buttons>
            {themeOptions?.map((option) => (
              <Tabs
                key={option?.key}
                variant={TAB_BUTTON_TYPE.STANDARD}
                title={option?.label}
                isActive={isActive[option?.key as keyof ThemeStatesType]}
                onClick={() => handleThemeChange(option?.theme as ThemeKey, option?.mode, option?.type)}
                applyFullWidth={true}
              />
            ))}
          </Buttons>
          <DropdownDivider />
          <DropdownItem
            label={workspace?.name}
            description='Add or Switch Organisation'
            onClick={() => {
              history.push(ROUTES.SELECT_WORKSPACE);
            }}
          />
          <DropdownDivider />
          <DropdownItem
            iconName='settings-icon'
            label='Settings'
            iconSize={16}
            onClick={onClickSetting}
            iconViewBox='0 0 18 18'
            iconTone='fill'
          />
          {mobile && (
            <HelpSupportDropdown
              setOpenContactUsModal={() => {
                setOpenContactUsModal(!openContactUsModal);
                workspaceNavDropdownRef.current?.closeDropdown();
              }}
              isMobile
            />
          )}
          <DropdownItem iconName='exit-icon' label='Log out' onClick={onClickLogout} iconViewBox='0 0 18 18' />
        </Dropdown>
      </MainDiv>
      {/* contact us Modal */}
      <Modal open={openContactUsModal} onClose={onCloseContactUsModal} maxWidth={'500px'}>
        <ContactUsModal
          openSuccessFullModal={() => setIsSuccessfulModalOpen(true)}
          closeContactUsModal={() => setOpenContactUsModal(false)}
        />
      </Modal>
      <Modal open={isSuccessfulModalOpen} onClose={onCloseSuccessfulModal} maxWidth={'500px'}>
        <SuccessFullModal onClose={onCloseSuccessfulModal} />
      </Modal>
    </>
  );
};
export default observer(ProfileDropdown);
